import Vue from 'vue';
import axios from "@/inside/axios";
import Action from "@/inside/models/Action/Action";
import TagModule from "@/inside/stores/actions/tags";
import {prepare} from "../imports/search";

export default {
    actions: {
        /**
         * Creates given action in Echoline API.
         *
         * @param {function} commit
         * @param {Action} action
         * @return {Promise}
         */
        create({commit}, action) {
            return new Promise((resolve, reject) => {
                axios.post("/actions", action, {
                    validateStatus: (status => status === 201)
                }).then((response) => {
                    action.id = response.data.id;
                    commit("setAction", action);
                    resolve(response);
                }).catch((error) => {
                    reject(error);
                });
            });
        },
        /**
         * Fetches all actions for current user and stores in module state as Action instances with Action.site property
         * hydrated with 'sites' module state data.
         *
         * @param {function} commit
         * @param {Array} rootGetters
         * @param {object} state
         * @return {Promise}
         */
        fetchAll({commit, rootGetters, state}) {
            return new Promise((resolve, reject) => {
                axios.get("/my/actions", {
                    validateStatus: (status) => {
                        return status === 200;
                    }
                }).then((response) => {
                    response.data.forEach((action_data) => {
                        const action = Action.fromAPI(action_data);
                        action.textbase = rootGetters["permissions/oneTextbase"](action_data.textbase.id);
                        commit("setAction", action);
                    });
                    resolve(Object.values(state.actions));
                }).catch((error) => {
                    reject(error);
                });
            });
        },
        /**
         * Fetches given action data from Echoline API.
         *
         * @param {function} commit
         * @param {Array} rootGetters
         * @param {string} action_id
         * @return {Promise}
         */
        fetchOne({commit, rootGetters}, action_id) {
            return new Promise((resolve, reject) => {
                axios.get(`/actions/${action_id}`, {
                    validateStatus: (status) => {
                        return status === 200;
                    }
                }).then((response) => {
                    const action = Action.fromAPI(response.data);
                    action.textbase = rootGetters["permissions/oneTextbase"](response.data.textbase.id);
                    commit("setAction", action);
                    resolve(action);
                }).then((error) => {
                    reject(error);
                });
            });
        },
        /**
         * @param {function} context
         * @param {object} action
         * @return {Promise}
         */
        update({commit}, action) {
            return new Promise((resolve, reject) => {
                axios.put(`/actions/${action.id}`, action, {
                    validateStatus: (status=> status === 200)
                }).then((response) => {
                    action.id = response.data.id;
                    commit("setAction", action);
                    resolve(response);
                }).catch((error) => {
                    reject(error);
                });
            });

        }
    },
    getters: {
        /**
         * Returns all actions stored in module state.
         *
         * @param {object} state
         * @return {[Action]}
         */
        all(state) {
            return Object.values(state.actions);
        },
        /**
         * Returns filtered actions, based on new filters model.
         * @param {object} state
         * @param {object} getters
         * @param {object} rootState
         * @param {Function[]} rootGetters
         * @return {Action[]}
         */
        filteredNG(state, getters, rootState, rootGetters) {
            let filteredTextbases = rootGetters["permissions/filteredTextbasesNG"].map((textbase) => {
                return textbase.id;
            });

            let filters = rootGetters["ngFilters/store"];
            const excludedTagIds = rootGetters["ngFilters/excludedTagIds"];

            // noinspection JSValidateTypes
            return Object.values(state.actions).filter((action) => {
                if (!filteredTextbases.includes(action.textbase.id)) {
                    return false;
                }

                if (filters.actionPriority.isExcluded(action.priority)) {
                    return false;
                }

                if (filters.actionStatus.isExcluded(action.status)) {
                    return false;
                }


                if (!filters.actionDuePeriod.isIncluded(action.date.due)) {
                    return false
                }

                const search = new RegExp(prepare(filters.inputSearch));

                const searchInAssignee = action.assignees.find((assignee) => {
                    if (typeof assignee.lastname === "string") {
                        if (prepare(assignee.lastname).match(search)) {
                            return true;
                        }
                    }

                    return !!(typeof assignee.email === "string"
                        && prepare(assignee.email).match(search));

                });
                const preparedTitle = prepare(action.title);
                if (!preparedTitle.match(search) && searchInAssignee === undefined) {
                    return false;
                }



                if (excludedTagIds.length > 0) {
                    if (action.tags.length === 0) {
                        return false;
                    }
                    if (action.tags.some((tag) => excludedTagIds.includes(tag.id))) {
                        return false;
                    }
                }

                return true;
            });
        },
        /**
         * Searches for an action corresponding to given action_id, and either returns a reference or a copy depending
         * on copy parameter value.
         *
         * @param {Object} state
         * @return {function(string, boolean): object|null}
         */
        one(state) {
            /**
             * @param {String} action_id
             * @param {Boolean} copy
             * @return {Object|null}
             */
            return (action_id, copy) => {
                if (state.actions.hasOwnProperty(action_id) === false) {
                    return null;
                }

                return (copy === true
                    ? JSON.parse(JSON.stringify(state.actions[action_id]))
                    : state.actions[action_id]
                );
            }
        }
    },
    mutations: {
        /**
         * @param {Object} state
         * @param {string} action_id
         */
        deleteAction(state, action_id){
            Vue.delete(state.actions, action_id)
        },
        /**
         * Stores or update given action in store.
         *
         * @param {object} state
         * @param {object} action
         */
        setAction(state, action) {
            (state.actions.hasOwnProperty(action.id) === false)
                ? Vue.set(state.actions, action.id, action)
                : state.actions[action.id] = action;
        }
    },
    modules: {
        tags: TagModule
    },
    namespaced: true,
    state: {
        actions: {}
    }
}
